import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useCreateGroup } from "@/hooks/useGroups";
import { humanErrorMessage } from "@/lib/entitlementError";

interface GroupChildDialogProps {
  /** The group to add a child under; null closes the dialog. */
  parent: { id: number; name: string } | null;
  organizationId: number;
  onClose: () => void;
}

/** Create a child group under `parent`. */
export default function GroupChildDialog({
  parent,
  organizationId,
  onClose,
}: GroupChildDialogProps) {
  const createGroup = useCreateGroup(organizationId);
  const [name, setName] = useState("");
  const [credit, setCredit] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (parent) {
      setName("");
      setCredit(false);
      setError(null);
    }
  }, [parent]);

  function handleSave() {
    if (!parent) return;
    setError(null);
    createGroup.mutate(
      { parentId: parent.id, name: name.trim(), creditPurchaseAllowed: credit },
      {
        onSuccess: () => onClose(),
        onError: (e) => setError(humanErrorMessage(e, "Failed to create group.")),
      },
    );
  }

  return (
    <Dialog
      open={!!parent}
      onClose={createGroup.isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>
        New group{parent ? ` under ${parent.name}` : ""}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField
            label="Group name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={credit}
                onChange={(e) => setCredit(e.target.checked)}
                size="small"
              />
            }
            label="Can purchase credit"
            slotProps={{ typography: { sx: { fontSize: 14 } } }}
          />
          {error && <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={createGroup.isPending}
          sx={{ color: "var(--color-graphite)" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={createGroup.isPending || name.trim().length === 0}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {createGroup.isPending ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
